import {FC, ReactNode, useCallback} from 'react';
import {styled} from 'styled-components';
import {useLocation} from 'wouter';

import {NonEmptyArray} from '@shared/lib/utils/type_utils';

import {MEDIA_QUERY_MOBILE_ONLY} from '@shared_web/breakpoints';
import {SvgIcon} from '@shared_web/components/core/svg_icon';
import {ZINDEX} from '@shared_web/lib/zindex';

import {TabWrapper} from '@mavismart_frontend/components/common_styles';
import {Translate, TranslateKey} from '@mavismart_frontend/components/translate';
import {icon_arrow_left_circle} from '@mavismart_frontend/icons/icon_arrow_left_circle';
import {
  HEADER_HEIGHT,
  MAIN_COLOR,
  TAB_HEIGHT,
  TAB_TITLE_HEIGHT,
} from '@mavismart_frontend/lib/constants';

export interface NavigationTab {
  path: string;
  label: TranslateKey;
  view: JSX.Element;
  default?: boolean;
  noPadding?: boolean;
}

interface NavigationTabsProps {
  tabs: NonEmptyArray<NavigationTab>;
  title?: ReactNode;
}

export const NavigationTabs: FC<NavigationTabsProps> = ({tabs, title}) => {
  const [location, navigate] = useLocation();
  const activeTab =
    tabs.find(tab => location.startsWith(tab.path)) ?? tabs.find(tab => tab.default);

  const handleTabClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const clickedPath = e.currentTarget.getAttribute('data-tab-path');
      if (clickedPath) {
        navigate(`~${clickedPath}`);
      }
    },
    [navigate]
  );

  const handleBack = useCallback(() => {
    history.back();
  }, []);

  return (
    <>
      {title !== undefined ? (
        <Title>
          <SvgIcon
            color="transparent"
            icon={icon_arrow_left_circle}
            onClick={handleBack}
            style={{minWidth: 24}}
          />
          <TitleText>{title}</TitleText>
        </Title>
      ) : (
        <></>
      )}
      <Tabs>
        {tabs.map(tab => {
          return (
            <NavStyled
              key={tab.label}
              $isSelected={activeTab?.path === tab.path}
              data-tab-path={tab.path}
              onClick={handleTabClick}
            >
              <Translate value={tab.label} />
            </NavStyled>
          );
        })}
      </Tabs>
      <View $hasTitle={title !== undefined}>
        <TabWrapper $noPadding={activeTab?.noPadding}>{activeTab?.view ?? <></>}</TabWrapper>
      </View>
    </>
  );
};

NavigationTabs.displayName = 'NavigationTabs';

const View = styled.div<{$hasTitle: boolean}>`
  height: 0;
  flex-grow: 1;
  overflow-y: auto;
  ${MEDIA_QUERY_MOBILE_ONLY} {
    margin-top: ${({$hasTitle}) => `calc(${HEADER_HEIGHT + ($hasTitle ? TAB_TITLE_HEIGHT : 0)}px + env(safe-area-inset-top))`};
  }
`;

const Title = styled.div`
  position: fixed;
  top: calc(${HEADER_HEIGHT + 5}px + env(safe-area-inset-top));
  width: 100%;
  height: ${TAB_TITLE_HEIGHT}px;
  line-height: ${TAB_TITLE_HEIGHT}px;
  background-color: white;
  font-size: 22px;
  font-weight: bolder;
  padding: 0 20px;
  z-index: ${ZINDEX.Tabs};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TitleText = styled.div`
  flex-grow: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Tabs = styled.div`
  display: flex;
  justify-content: space-between;
  position: sticky;
  top: calc(${HEADER_HEIGHT}px + env(safe-area-inset-top));
  height: ${TAB_HEIGHT}px;
  min-height: ${TAB_HEIGHT}px;
  width: 100%;
  background-color: white;
  z-index: ${ZINDEX.Tabs};
`;

const NavStyled = styled.div<{$isSelected: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
 
  cursor: pointer;
  border-bottom: ${({$isSelected}) => ($isSelected ? `2px solid ${MAIN_COLOR}` : '2px solid #EEF1F4')};
  transition: border-color 0.3s;
  font-size: 16px;
`;
